'use client';

import { useCallback } from 'react';
import { PublicKey, User } from '@velocity-exchange/sdk';
import {
	getUserKey,
	fetchUserClientsAndAccounts,
} from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { useGenerateInitialStateForUserAccount } from '../useGenerateInitialStateForUserAccount';
import invariant from 'tiny-invariant';

/**
 * The `update` handler already registered for each `User`, so re-running setup
 * (e.g. after recovering a dead subscription) rewires rather than stacks up
 * duplicate listeners.
 */
const registeredUpdateHandlers = new WeakMap<User, () => void>();

/**
 * Hook for setting up user account state and listeners.
 * Handles account initialization and event subscriptions.
 * Note: margin info, positions, orders, and leftover isolated positions
 * are now handled by React Query (AccountTradingDataSync).
 */
export const useSetupUserAccountStateAndListeners = () => {
	const getVelocityStore = useVelocityStore((s) => s.get);
	const setVelocityAccountsStore = useVelocityAccountsStore((s) => s.set);
	const generateInitialStateForUserAccount =
		useGenerateInitialStateForUserAccount();

	const subscribeToUserAccountUpdates = useCallback(
		(user: User) => {
			const handleUserUpdate = () => {
				const isSubscribed = user.isSubscribed;
				if (!isSubscribed) return;

				const userId = user.getUserAccountOrThrow().subAccountId;
				const userAuthority = user.getUserAccountOrThrow().authority;
				const userKey = getUserKey(userId, userAuthority);
				const delegate = user.getUserAccountOrThrow().delegate;

				setVelocityAccountsStore((s) => {
					if (s.accounts[userKey]) {
						s.accounts[userKey].delegate = delegate;
						s.accounts[userKey].isDelegatedAway =
							!delegate.equals(DEFAULT_PUBLIC_KEY);
					}
				});
			};

			const previous = registeredUpdateHandlers.get(user);
			if (previous) {
				user.eventEmitter.removeListener('update', previous);
			}
			registeredUpdateHandlers.set(user, handleUserUpdate);
			user.eventEmitter.on('update', handleUserUpdate);
		},
		[setVelocityAccountsStore]
	);

	// Main function: Setup user account state and listeners
	const setupUserAccountStateAndListeners = useCallback(
		(
			subAccountPubKey?: PublicKey,
			opts?: { newAccountId?: number },
			emulate?: boolean
		) => {
			try {
				const velocityClient = getVelocityStore().velocityClient.client;
				invariant(velocityClient, 'Failed to get drift client');

				const walletAdapter = getVelocityStore().wallet.current.adapter;
				const walletPubkey = walletAdapter?.publicKey;
				invariant(walletPubkey, 'Failed to get wallet pubkey');

				const allUsers = fetchUserClientsAndAccounts(velocityClient);

				// A subaccount whose subscription is down cannot be read, but it must
				// not take the healthy ones down with it.
				const subscribedUsers = allUsers.filter(
					(user) => user.user.isSubscribed
				);

				if (subscribedUsers.length === 0 && allUsers.length > 0)
					throw new Error(
						'Accounts need to be subscribed before calling setupUserAccountStateAndListeners'
					);

				// Generate state for accounts
				const userAccountStates = subscribedUsers
					.map((subAccountUser) => {
						return generateInitialStateForUserAccount(
							subAccountUser.user,
							subAccountUser.userAccount
						);
					})
					.filter((accountState) => !!accountState);

				// Add user accounts state to store
				setVelocityAccountsStore((s) => {
					userAccountStates.forEach((accountState) => {
						s.accounts[accountState.userKey] = accountState;
					});
				});

				// add update handler to userAccount event emitter
				subscribedUsers.forEach((userObject) => {
					subscribeToUserAccountUpdates(userObject.user);
				});

				if (emulate) {
					setVelocityAccountsStore((s) => {
						s.currentUserKey = userAccountStates[0]?.userKey ?? '';
					});
					velocityClient.switchActiveUser(
						userAccountStates[0]?.userId ?? 0,
						userAccountStates[0]?.authority ?? walletPubkey
					);
				}

				if (subAccountPubKey) {
					const selectedAccountData: AccountData | undefined =
						userAccountStates.find((acct) =>
							acct.pubKey.equals(subAccountPubKey)
						);

					if (selectedAccountData) {
						setVelocityAccountsStore((s) => {
							s.currentUserKey = selectedAccountData.userKey;
						});
						velocityClient.switchActiveUser(
							selectedAccountData.userId,
							selectedAccountData.authority
						);
					}
				}
			} catch (err: unknown) {
				console.error(err);
			}
		},
		[
			generateInitialStateForUserAccount,
			setVelocityAccountsStore,
			subscribeToUserAccountUpdates,
		]
	);

	return { setupUserAccountStateAndListeners };
};
